
import React, { useState, useEffect } from 'react';
import { Package, Truck, Clock, MapPin, Search, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';
import { ADD_TO_CART_TOAST_DURATION } from '../lib/toastConfig';
import { useAuth } from '../contexts/AuthContext';
import { useOrders } from "../contexts/OrdersContext";
import { formatPrice } from "../utils/productPricing";

const OrderHistoryPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  
  const { addToCart } = useCart();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { orders, loading, error, refreshOrders } = useOrders();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    }
  }, [authLoading, user, navigate]);

  const toggleOrderExpansion = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const getStatusColor = (status) => {
    const normalizedStatus = status?.toLowerCase() || 'processing';
    
    switch (normalizedStatus) {
      case 'processing': 
      case 'pending':
        return 'text-amber-400 bg-amber-400/10 border-amber-400/30';
      case 'shipped': 
      case 'shipping':
        return 'text-blue-400 bg-blue-400/10 border-blue-400/30';
      case 'delivered': 
      case 'completed':
        return 'text-green-400 bg-green-400/10 border-green-400/30';
      case 'cancelled': 
      case 'canceled':
        return 'text-red-400 bg-red-400/10 border-red-400/30';
      default: 
        return 'text-amber-400 bg-amber-400/10 border-amber-400/30'; // Default to processing
    }
  };

  const getStatusDisplay = (status) => {
    if (!status) return 'Processing';
    
    const normalizedStatus = status.toLowerCase();
    const statusMap = {
      'processing': 'Processing',
      'pending': 'Processing', 
      'shipped': 'Shipped',
      'shipping': 'Shipped',
      'delivered': 'Delivered',
      'completed': 'Delivered',
      'cancelled': 'Cancelled',
      'canceled': 'Cancelled'
    };
    
    return statusMap[normalizedStatus] || 'Processing';
  };

  const getTimeFilterOptions = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const lastYear = currentYear - 1;
    
    return [
      { value: 'all', label: 'All Time' },
      { value: 'last30', label: 'Last 30 Days' },
      { value: 'last90', label: 'Last 3 Months' },
      { value: 'thisYear', label: `${currentYear}` },
      { value: 'lastYear', label: `${lastYear}` },
    ];
  };

  const filterOrders = () => {
    if (!orders || orders.length === 0) return [];
    
    let filtered = [...orders];

    if (searchTerm) {
      filtered = filtered.filter(order => 
        order.id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.items && order.items.some(item => 
          item.name?.toLowerCase().includes(searchTerm.toLowerCase())
        ))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => 
        order.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (timeFilter !== 'all') {
      const now = new Date();
      const orderDate = (order) => new Date(order.date || order.created_at);
      
      switch (timeFilter) {
        case 'last30':
          filtered = filtered.filter(order => 
            (now - orderDate(order)) <= 30 * 24 * 60 * 60 * 1000
          );
          break;
        case 'last90':
          filtered = filtered.filter(order => 
            (now - orderDate(order)) <= 90 * 24 * 60 * 60 * 1000
          );
          break;
        case 'thisYear':
          filtered = filtered.filter(order => 
            orderDate(order).getFullYear() === now.getFullYear()
          );
          break;
        case 'lastYear':
          filtered = filtered.filter(order => 
            orderDate(order).getFullYear() === now.getFullYear() - 1
          );
          break;
        default:
          break;
      }
    }

    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.date || a.created_at) - new Date(b.date || b.created_at));
        break;
      case 'highest':
        filtered.sort((a, b) => (b.total || 0) - (a.total || 0));
        break;
      case 'lowest':
        filtered.sort((a, b) => (a.total || 0) - (b.total || 0));
        break;
      default:
        break;
    }

    return filtered;
  };

  const reorderItem = (item) => {
    const cartItem = {
      id: item.id || item.product_id,
      name: item.name || item.product_name,
      price: item.price,
      image: item.image,
      selectedColor: item.selectedColor || item.selected_color,
      category: item.category || item.product_category,
      sku: item.sku || item.product_sku
    };
    
    addToCart(cartItem);
    toast.success(`${cartItem.name} has been added to your cart.`, {
      duration: ADD_TO_CART_TOAST_DURATION,
    });
  };

  const reorderEntireOrder = (order) => {
    if (!order.items || order.items.length === 0) {
      toast.error('No items found in this order to reorder.');
      return;
    }
    
    order.items.forEach(item => {
      reorderItem(item);
    });
    toast.success(`All ${order.items.length} items from your previous order have been added to your cart.`, {
      duration: ADD_TO_CART_TOAST_DURATION,
    });
  };

  const filteredOrders = filterOrders();
  const totalOrders = orders?.length || 0;
  const totalSpent = orders?.reduce((sum, order) => sum + (order.total || 0), 0) || 0;

  console.log("OrdersContext result:", {
    orders,
    loading,
    error,
  });

  console.log('🔍 OrderHistoryPage render:', {
    user: user?.id,
    ordersCount: orders?.length,
    loading,
    filteredCount: filteredOrders.length
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading your orders...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl font-light text-white mb-2">Please Log In</h2>
          <p className="text-gray-400">You need to be logged in to view your orders.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <Package className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-white mb-3">
            Orders unavailable
          </h2>
          <p className="text-sm text-gray-400 mb-5">{error}</p>
          <button
            type="button"
            onClick={refreshOrders}
            className="rounded-full border border-amber-400/40 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-amber-300 transition-colors hover:bg-amber-400/10"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_25%,rgba(251,191,36,0.03)_0%,transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_75%,rgba(251,191,36,0.02)_0%,transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(135deg,transparent_0%,rgba(251,191,36,0.01)_50%,transparent_100%)]"></div>
      </div>

      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/5 w-1 h-1 bg-amber-400/30 rounded-full animate-pulse"></div>
        <div className="absolute top-3/5 right-1/4 w-0.5 h-0.5 bg-amber-300/40 rounded-full animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-3/4 w-1 h-1 bg-amber-500/20 rounded-full animate-pulse delay-2000"></div>
      </div>
      
      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 mb-4 sm:mb-6 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-400/20 to-amber-600/20 rounded-full blur-xl"></div>
            <div className="relative w-full h-full border border-amber-400/30 rounded-full flex items-center justify-center backdrop-blur-sm">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full shadow-lg shadow-amber-400/25"></div>
            </div>
          </div>

          <h1 className="mb-5 text-sm font-medium uppercase tracking-[0.28em] text-white sm:text-base md:text-lg">
            Order History
          </h1>

          <div className="flex items-center justify-center gap-4 sm:gap-8 text-gray-400 mb-6 sm:mb-8">
            <div className="text-center">
              <div className="text-xl font-semibold text-white sm:text-2xl">{totalOrders}</div>
              <div className="text-[10px] font-medium uppercase tracking-[0.16em] sm:text-xs">Total Orders</div>
            </div>
            <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
            <div className="text-center">
              <div className="text-xl font-semibold text-amber-400 sm:text-2xl">{formatPrice(totalSpent)}</div>
              <div className="text-[10px] font-medium uppercase tracking-[0.16em] sm:text-xs">Total Spent</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-slate-800/30 backdrop-blur-sm border border-amber-400/20 rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 shadow-2xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
            <div className="sm:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search orders or products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-3 bg-slate-700/50 border border-gray-600/30 rounded-lg text-sm font-medium text-white placeholder-gray-400 focus:border-amber-400/50 focus:outline-none transition-colors"
                />
              </div>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 sm:px-4 py-2 sm:py-3 bg-slate-700/50 border border-gray-600/30 rounded-lg text-xs font-semibold uppercase tracking-wider text-white focus:border-amber-400/50 focus:outline-none transition-colors sm:text-sm"
            >
              <option value="all">All Status</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              className="px-3 sm:px-4 py-2 sm:py-3 bg-slate-700/50 border border-gray-600/30 rounded-lg text-xs font-semibold uppercase tracking-wider text-white focus:border-amber-400/50 focus:outline-none transition-colors sm:text-sm"
            >
              {getTimeFilterOptions().map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 sm:px-4 py-2 sm:py-3 bg-slate-700/50 border border-gray-600/30 rounded-lg text-xs font-semibold uppercase tracking-wider text-white focus:border-amber-400/50 focus:outline-none transition-colors sm:text-sm"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="highest">Highest Value</option>
              <option value="lowest">Lowest Value</option>
            </select>
          </div>
        </div>

        {/* Orders List */}
        <div className="space-y-4 sm:space-y-6">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <Package className="w-12 h-12 sm:w-16 sm:h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.18em] text-white sm:text-base">
                {totalOrders === 0 ? 'No orders yet' : 'No orders found'}
              </h3>
              <p className="text-xs font-medium text-gray-400 sm:text-sm">
                {totalOrders === 0 
                  ? 'When you place your first order, it will appear here' 
                  : 'Try adjusting your filters or search terms'
                }
              </p>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <div key={order.id} className="bg-slate-800/30 backdrop-blur-sm border border-amber-400/20 rounded-2xl shadow-2xl overflow-hidden">
                
                <div className="p-4 sm:p-6 border-b border-gray-700/50">
                  <div className="flex flex-col lg:flex-row items-start justify-between gap-4 mb-4">
                    {/* Left side - Order info and product preview */}
                    <div className="flex items-start gap-3 sm:gap-4 flex-1 w-full">
                      {/* Product images preview */}
                      <div className="flex -space-x-2 flex-shrink-0">
                        {order.items && order.items.length > 0 ? (
                          <>
                            {order.items.slice(0, 3).map((item, index) => (
                              <img 
                                key={item.id || index}
                                src={item.image || '/api/placeholder/40/40'} 
                                alt={item.name || 'Product'}
                                loading="lazy"
                                decoding="async"
                                className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded-lg border-2 border-slate-800 shadow-lg"
                                style={{ zIndex: order.items.length - index }}
                                onError={(e) => {
                                  e.target.src = '/api/placeholder/40/40';
                                }}
                              />
                            ))}
                            {order.items.length > 3 && (
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-700/50 border-2 border-slate-800 rounded-lg flex items-center justify-center text-xs text-gray-300">
                                +{order.items.length - 3}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-700/50 border-2 border-slate-800 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                          <h3 className="text-lg sm:text-xl font-light text-white truncate">
                            ORD-{order.id?.toString().slice(-6).toUpperCase() || 'UNKNOWN'}
                          </h3>
                          <div className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${getStatusColor(order.status)} self-start sm:self-auto`}>
                            {getStatusDisplay(order.status)}
                          </div>
                        </div>
                        
                        {/* Product names preview */}
                        <div className="text-sm text-gray-300 space-y-1">
                          {order.items && order.items.length > 0 ? (
                            <>
                              {order.items.slice(0, 2).map((item, index) => (
                                <div key={item.id || index} className="flex items-center gap-2">
                                  <span className="text-amber-400 flex-shrink-0">•</span>
                                  <span className="truncate">{item.name || 'Unknown Product'}</span>
                                  {item.quantity > 1 && (
                                    <span className="text-xs text-gray-400 flex-shrink-0">(x{item.quantity})</span>
                                  )}
                                </div>
                              ))}
                              {order.items.length > 2 && (
                                <div className="flex items-center gap-2 text-gray-400">
                                  <span className="flex-shrink-0">•</span>
                                  <span className="text-xs">+{order.items.length - 2} more items</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center gap-2 text-gray-400">
                              <span className="text-amber-400 flex-shrink-0">•</span>
                              <span className="text-xs">No items found</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Right side - Total and item count */}
                    <div className="text-left lg:text-right w-full lg:w-auto">
                      <div className="text-xl sm:text-2xl font-light text-amber-400">
                        {formatPrice(order.total || 0)}
                      </div>
                      <div className="text-sm text-gray-400">
                        {order.items?.length || 0} items
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-sm text-gray-400 mb-4 gap-2">
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 flex-shrink-0" />
                        <span className="text-xs sm:text-sm">
                          Ordered: {new Date(order.date || order.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {order.estimatedDelivery && (
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 flex-shrink-0" />
                          <span className="text-xs sm:text-sm">
                            Est. Delivery: {new Date(order.estimatedDelivery).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                    {order.shippingAddress && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span className="text-xs sm:text-sm truncate">
                          {order.shippingAddress.city}, {order.shippingAddress.state}
                        </span>
                      </div>
                    )}
                  </div>

                  {(order.tracking_number || order.label_url) && (
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      {order.tracking_number && (
                        <a
                          href={order.tracking_url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-1.5 bg-blue-400/10 hover:bg-blue-400/20 border border-blue-400/30 rounded-lg text-blue-400 hover:text-blue-300 transition-all duration-300 text-xs sm:text-sm"
                        >
                          <Truck className="w-4 h-4" />
                          Track: {order.tracking_number}
                        </a>
                      )}
                      {order.label_url && (
                        <a
                          href={order.label_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-1.5 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/30 rounded-lg text-amber-400 hover:text-amber-300 transition-all duration-300 text-xs sm:text-sm"
                        >
                          <Package className="w-4 h-4" />
                          Shipping Label (PDF)
                        </a>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                    <button
                      onClick={() => toggleOrderExpansion(order.id)}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-700/50 hover:bg-slate-700/70 border border-gray-600/30 rounded-lg text-gray-300 hover:text-white transition-all duration-300 text-sm sm:text-base"
                    >
                      {expandedOrders.has(order.id) ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          View Details
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => reorderEntireOrder(order)}
                      disabled={!order.items || order.items.length === 0}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/30 rounded-lg text-amber-400 hover:text-amber-300 transition-all duration-300 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Clock className="w-4 h-4" />
                      Reorder All
                    </button>
                  </div>
                </div>

                {expandedOrders.has(order.id) && (
                  <div className="p-4 sm:p-6 bg-slate-700/40 border-t border-slate-600">
                    {order.items && order.items.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
                        {order.items.map((item, index) => (
                          <div key={item.id || index} className="bg-slate-700/20 border border-gray-600/30 rounded-xl p-3 sm:p-4">
                            <div className="flex gap-3 mb-3">
                              <img 
                                src={item.image || '/api/placeholder/60/60'} 
                                alt={item.name || 'Product'}
                                loading="lazy"
                                decoding="async"
                                className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg border border-gray-600/30 flex-shrink-0"
                                onError={(e) => {
                                  e.target.src = '/api/placeholder/60/60';
                                }}
                              />
                              <div className="flex-1 min-w-0">
                                <h4 className="text-white font-medium text-sm line-clamp-2">
                                  {item.name || 'Unknown Product'}
                                </h4>
                                <div className="text-xs text-gray-400 mt-1">
                                  {(item.selectedColor?.name || item.selectedColor || 'No color')} • {item.category || 'Uncategorized'}
                                </div>
                                <div className="text-amber-400 font-light mt-1 text-sm sm:text-base">
                                  {formatPrice(item.price || 0)}
                                </div>
                              </div>
                            </div>

                            <div className="space-y-2 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-400">Quantity:</span>
                                <span className="text-white">{item.quantity || 1}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Subtotal:</span>
                                <span className="text-white">
                                  {formatPrice((item.price || 0) * (item.quantity || 1))}
                                </span>
                              </div>
                            </div>

                            <button 
                              onClick={() => reorderItem(item)}
                              className="w-full mt-3 py-2 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/30 rounded-lg text-amber-400 hover:text-amber-300 transition-all duration-300 text-xs sm:text-sm"
                            >
                              Reorder Item
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-400">
                        <Package className="w-8 h-8 mx-auto mb-2" />
                        <p>No items found in this order</p>
                      </div>
                    )}

                    {order.shippingAddress && (
                      <div className="bg-slate-700/20 rounded-lg p-3 sm:p-4">
                        <h4 className="text-white font-medium mb-2 flex items-center gap-2 text-sm sm:text-base">
                          <MapPin className="w-4 h-4 text-amber-400" />
                          Shipping Address
                        </h4>
                        <div className="text-gray-300 text-xs sm:text-sm">
                          <div>{order.shippingAddress.address}</div>
                          <div>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}</div>
                          <div>{order.shippingAddress.country}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {filteredOrders.length > 0 && (
          <div className="text-center mt-6 sm:mt-8 text-gray-400 text-sm sm:text-base">
            Showing {filteredOrders.length} of {totalOrders} orders
          </div>
        )}
      </div>

      {/* Decorative Elements */}
      <div className="absolute left-0 top-1/2 transform -translate-y-1/2">
        <div className="w-px h-24 bg-gradient-to-b from-transparent via-amber-400/30 to-transparent"></div>
      </div>
      <div className="absolute right-0 top-1/2 transform -translate-y-1/2">
        <div className="w-px h-24 bg-gradient-to-b from-transparent via-amber-400/30 to-transparent"></div>
      </div>
    </div>
  );
};

export default OrderHistoryPage;
